import type {
    ComponentStageData,
    ComponentStageHeuristicDefinition,
    ConversionDal,
    PluginContext,
    PluginStageHandler,
    ReadOnlyExtensionAPI
} from '../types'
import {
    getMeasurementsPointer,
    getSourceLayoutPointer,
    getStructurePointer,
    getStylePointer,
    getTargetLayoutPointer,
    getTransformationsPointer
} from '../conversionDal/utils/pointer'
import type {LayoutEntryItem, StructureEntryItem, StyleEntryItem, TransformationEntryItem} from '../conversionDal/types'
import {LayoutIntentType, type Pointer, type SingleLayoutData} from '@wix/document-services-types'
import {pointerUtils} from '@wix/document-manager-core'
import {type AlgorithmModes, Orientation} from '../enums'
import {SemanticTypes} from '../preprocess/conversionDataBuilders'

const getDefinedPaths = (obj: Record<string, any>, prefix?: string): Set<string> => {
    const paths = []

    for (const key in obj) {
        if (!obj.hasOwnProperty(key)) {
            continue
        }

        const value = obj[key]
        const path = prefix ? `${prefix}/${key}` : key

        if (Array.isArray(value)) {
            paths.push(path)
        } else if (typeof value === 'object' && value !== null) {
            paths.push(...getDefinedPaths(value, path))
        } else {
            paths.push(path)
        }
    }

    return new Set(paths)
}

export const hasMenuBrother = (componentPointer: Pointer, conversionDal: ConversionDal) => {
    const component = conversionDal.get(componentPointer)
    const {components} = component
    return components?.some(
        (childId: string) => conversionDal.get(getStructurePointer({id: childId})).semanticType === SemanticTypes.MENU
    )
}

export const updateTargetLayout = (
    {conversionDal, componentPointer}: Pick<ComponentStageData, 'conversionDal' | 'componentPointer'>,
    layout: Partial<SingleLayoutData>
) => {
    conversionDal.modify(getTargetLayoutPointer(componentPointer), (value: LayoutEntryItem) => {
        const definedPaths = getDefinedPaths(layout)

        return {
            ...value,
            ...{
                variableConnections: value?.variableConnections?.filter(({path}) => !definedPaths.has(path))
            },
            ...layout
        }
    })
}

export const updateTransformations = (
    {conversionDal, componentPointer}: ComponentStageData,
    transformation: Partial<TransformationEntryItem>
) => {
    conversionDal.modify(getTransformationsPointer(componentPointer), (value: TransformationEntryItem) => ({
        ...value,
        ...transformation
    }))
}

export const updateStyle = ({conversionDal, componentPointer}: ComponentStageData, style: Partial<StyleEntryItem>) => {
    conversionDal.modify(getStylePointer(componentPointer), (value: StyleEntryItem) => ({...value, ...style}))
}

export const createAdaptiveHeuristic =
    <T extends ComponentStageData>(
        fallbackCallback: PluginStageHandler<T>,
        adaptiveCallback?: PluginStageHandler<T>
    ): PluginStageHandler<T> =>
    (stageData: T, contextHelper: PluginContext) => {
        const {initialSnapshot, componentPointer} = stageData

        if (!initialSnapshot.getValue(getTargetLayoutPointer(componentPointer))) {
            return fallbackCallback(stageData, contextHelper)
        }

        if (adaptiveCallback) {
            return adaptiveCallback(stageData, contextHelper)
        }
    }

export const isSection = ({
    conversionDal,
    componentPointer
}: Pick<ComponentStageData, 'conversionDal' | 'componentPointer'>) => {
    const component = conversionDal.get(componentPointer)
    return [SemanticTypes.SECTION, SemanticTypes.HEADER_SECTION].includes(component.semanticType)
}

export const getParent = (conversionDal: ConversionDal, componentPointer: Pointer): Pointer | undefined => {
    const structure: StructureEntryItem = conversionDal.get(componentPointer)
    const parentPointer = pointerUtils.getPointerFromPointer(structure.parent, componentPointer)

    if (!conversionDal.get(parentPointer)) {
        return
    }

    return parentPointer
}

export const isSlottedComponent = (
    componentStageData: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>
) => {
    const {conversionDal, componentPointer} = componentStageData
    return conversionDal.get(componentPointer)?.isSlottedComponent
}

export const isPlugin = (componentStageData: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>) => {
    const {conversionDal, componentPointer} = componentStageData
    const parent = getParent(conversionDal, componentPointer)

    return parent && isSlottedComponent({...componentStageData, componentPointer: parent})
}

export const isMenuContainerChild = (componentPointer: Pointer, conversionDal: ConversionDal) => {
    const parent = getParent(conversionDal, componentPointer)
    if (!parent) return false
    const grandParent = getParent(conversionDal, parent)
    if (!grandParent) return false
    const component = conversionDal.get(grandParent)
    return component.semanticType === SemanticTypes.MENU
}

export const isInternalBox = ({
    conversionDal,
    componentPointer
}: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>) =>
    conversionDal.get(componentPointer)?.semanticType === SemanticTypes.INTERNAL_BOX

export const isBoxPlugin = ({
    conversionDal,
    componentPointer
}: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>) =>
    conversionDal.get(componentPointer)?.semanticType === SemanticTypes.BOX_PLUGIN

export const isEmptyBox = (componentStageData: ComponentStageData) => {
    const {componentPointer, conversionDal} = componentStageData
    const component = conversionDal.get(componentPointer)

    return (isInternalBox(componentStageData) || isBoxPlugin(componentStageData)) && !component.components?.length
}

export const isSuperGrid = ({
    conversionDal,
    componentPointer
}: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>) => {
    return conversionDal.get(componentPointer)?.layoutIntent?.type === LayoutIntentType.SuperGrid
}

export const isSuperGridCell = ({
    conversionDal,
    componentPointer
}: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>) => {
    const parentPointer = getParent(conversionDal, componentPointer)
    return (
        !!parentPointer &&
        conversionDal.get(componentPointer)?.semanticType === SemanticTypes.BOX &&
        isSuperGrid({componentPointer: parentPointer, conversionDal})
    )
}

export const isSuperGridCellRootContainer = ({
    conversionDal,
    componentPointer
}: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>) => {
    const parentPointer = getParent(conversionDal, componentPointer)
    return (
        !!parentPointer &&
        conversionDal.get(componentPointer)?.semanticType === SemanticTypes.INTERNAL_BOX &&
        isSuperGridCell({componentPointer: parentPointer, conversionDal})
    )
}

export const isSectionsRootContainer = (
    componentStageData: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>
) => {
    const {conversionDal, componentPointer} = componentStageData
    const parentPointer = getParent(conversionDal, componentPointer)

    return (
        !!parentPointer &&
        isInternalBox(componentStageData) &&
        [SemanticTypes.HEADER_SECTION, SemanticTypes.SECTION].includes(conversionDal.get(parentPointer).semanticType)
    )
}

export const isHeaderContainer = (
    componentStageData: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>
) => {
    const {conversionDal, componentPointer} = componentStageData

    if (!isSectionsRootContainer(componentStageData)) {
        return false
    }

    const parentPointer = getParent(conversionDal, componentPointer)

    if (!parentPointer) {
        return false
    }

    return conversionDal.get(parentPointer)?.semanticType === SemanticTypes.HEADER_SECTION
}

export const isChildOfHeader = (componentPointer: Pointer, conversionDal: ConversionDal) => {
    const parentPointer = getParent(conversionDal, componentPointer)
    if (!parentPointer) return false
    return isHeaderContainer({componentPointer: parentPointer, conversionDal})
}

export const isHeaderFirstRowChildComponent = (componentPointer: Pointer, conversionDal: ConversionDal) => {
    if (!isChildOfHeader(componentPointer, conversionDal)) return false
    const {itemLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer))
    return itemLayout?.gridArea?.rowStart === 1
}

export const isPinned = (componentStagedData: Pick<ComponentStageData, 'conversionDal' | 'componentPointer'>) => {
    const {conversionDal, componentPointer} = componentStagedData
    const layout = conversionDal.get(getSourceLayoutPointer(componentPointer))
    return layout?.itemLayout?.type === 'FixedItemLayout' || layout?.itemLayout?.pinnedToContainer === true
}

export const isPinnedToContainer = (componentStagedData: ComponentStageData) => {
    const {conversionDal, componentPointer} = componentStagedData
    return !!getParent(conversionDal, componentPointer)
}

export const isEmptyRootContainer = (
    componentStageData: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>
) => {
    const {componentPointer, conversionDal} = componentStageData
    const component = conversionDal.get(componentPointer)
    return isSectionsRootContainer(componentStageData) && !component.components?.length
}

export const isZeroHeightOrEmptySection = (
    componentStageData: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>
) => {
    const {componentPointer, conversionDal} = componentStageData
    const component = conversionDal.get(componentPointer)

    if ([SemanticTypes.HEADER_SECTION, SemanticTypes.SECTION].includes(component.semanticType)) {
        if (conversionDal.get(getMeasurementsPointer(componentPointer)).height === 0) {
            return true
        }

        const [childId] = component.components

        return isEmptyRootContainer({componentPointer: getStructurePointer({id: childId}), conversionDal})
    }

    return false
}

export const predicatePinnedHeuristic = (
    componentStagedData: ComponentStageData,
    horizontalOrVertical: Orientation,
    min: number,
    max: number | undefined,
    predicate: (justifySelf: 'start' | 'center' | 'end', alignSelf: 'start' | 'center' | 'end') => boolean
) => {
    const {conversionDal, componentPointer} = componentStagedData
    if (!isPinned(componentStagedData)) return false
    const layout = conversionDal.get(getSourceLayoutPointer(componentPointer))
    const {itemLayout} = layout
    const {justifySelf, alignSelf, margins} = itemLayout
    const {left, right, bottom, top} = margins
    const marginVal =
        horizontalOrVertical === Orientation.Horizontal ? left?.value || right?.value : top?.value || bottom?.value
    return predicate(justifySelf, alignSelf) && marginVal >= min && (!max || marginVal <= max)
}

export const findHeuristic = (
    heuristics: ComponentStageHeuristicDefinition[],
    componentStageData: ComponentStageData,
    contextHelper?: PluginContext,
    readonlyExtensionAPI?: ReadOnlyExtensionAPI
): ComponentStageHeuristicDefinition =>
    heuristics.find(({predicate}) => predicate(componentStageData, contextHelper, readonlyExtensionAPI))!

export const generatePluginHandlerPerMode =
    (
        modeToRunnerMap: Record<AlgorithmModes, PluginStageHandler<ComponentStageData>>
    ): PluginStageHandler<ComponentStageData> =>
    (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
        modeToRunnerMap[componentStageData.config.mode]?.(componentStageData, contextHelper)

export const isBackgroundComponent = ({conversionDal, componentPointer}: ComponentStageData): boolean => {
    const layout = conversionDal.get(getSourceLayoutPointer(componentPointer))
    return layout?.itemLayout?.type === 'BackgroundLayerItemLayout'
}
